/**
 * TerminalView — live tail of the specialist's commands (wb_exec): the command, its output in
 * mono, and an exit badge with duration. Follows new output unless the user scrolled up.
 */

import { useEffect, useRef } from 'react';
import { CircleCheck, CircleX, LoaderCircle, Clock } from 'lucide-react';
import { formatDuration } from './agentUi.js';
import './WorkbenchDrawer.css';

const STICK_PX = 32;

function ExitBadge({ exec }) {
  if (exec.exit_code === null || exec.exit_code === undefined) {
    return <span className="wbd__exit is-run"><LoaderCircle size={11} aria-hidden="true" /> running</span>;
  }
  if (exec.timed_out) return <span className="wbd__exit is-bad"><Clock size={11} aria-hidden="true" /> timed out</span>;
  const ok = exec.exit_code === 0;
  const ExitIcon = ok ? CircleCheck : CircleX;
  return (
    <span className={`wbd__exit ${ok ? 'is-good' : 'is-bad'}`}>
      <ExitIcon size={11} aria-hidden="true" /> exit {exec.exit_code}{exec.duration_ms !== null ? ` · ${formatDuration(exec.duration_ms)}` : ''}
    </span>
  );
}

export default function TerminalView({ execs = [] }) {
  const ref = useRef(null);
  const stick = useRef(true);
  const lineCount = execs.reduce((n, e) => n + e.lines.length, 0);
  const tail = execs.length ? `${execs[execs.length - 1].exec_id}|${execs[execs.length - 1].exit_code}` : '';

  useEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [lineCount, tail]);

  return (
    <div
      className="wbd__terminal wbd__scroller"
      ref={ref}
      onScroll={() => {
        const el = ref.current;
        if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_PX;
      }}
      role="log"
      aria-label="Terminal"
    >
      {execs.length === 0 && <div className="wbd__empty">No commands yet.</div>}
      {execs.map((e) => (
        <div key={e.exec_id} className="wbd__exec">
          <div className="wbd__cmd">
            <span className="wbd__prompt">$</span>
            <span className="wbd__cmd-text">{e.cmd}</span>
            <ExitBadge exec={e} />
          </div>
          {e.lines.map((l, i) => (
            // Lines have no ids and are only ever appended (or trimmed from the front of the case).
            <div key={i} className={`wbd__line${l.s === 'stderr' ? ' is-err' : ''}`}>{l.t || ' '}</div>
          ))}
        </div>
      ))}
    </div>
  );
}
